/**
 * VoxelDebug - Visual debugging tools for voxel terrain
 *
 * Provides:
 * - Chunk boundary wireframes (8m × 8m × 8m boxes)
 * - Chunk coordinate labels (`x,y,z,lod`)
 */

import * as THREE from 'three';
import { CHUNK_WORLD_SIZE } from '@worldify/shared';
import { Chunk } from './Chunk.js';
import { ChunkGeometry } from './ChunkGeometry.js';
import { lodDebugCss } from '../material/LodDebugMaterials.js';

// ============== Debug Colors ==============
/** Green - chunk is on screen */
export const COLOR_HAS_MESH = 0x00ff00;
/** Yellow - chunk is resident but has no mesh (margin source, occluder shell, parked in the queue) */
export const COLOR_EMPTY = 0xffff00;
/** Magenta - meshed and NOT drawn: a mesh job spent on nothing (see ChunkBoundsState). */
export const COLOR_MESHED_HIDDEN = 0xff00ff;

/**
 * What a bounds box reports. Green/yellow used to mean "has a mesh or not", which put the drawn
 * chunks and the meshed-but-hidden ones in the SAME colour — and that difference is the whole cost
 * question: measured on a settled cold load, 200 of 421 lod-0 meshes were never drawn (99 suppressed
 * by a displayed lod-1 parent, 93 from the mesh gate's +1 face ring). Three states make that
 * readable instead of hidden inside the green.
 */
export type ChunkBoundsState = 'drawn' | 'meshedHidden' | 'unmeshed';

export function chunkBoundsColor(state: ChunkBoundsState): number {
  if (state === 'drawn') return COLOR_HAS_MESH;
  return state === 'meshedHidden' ? COLOR_MESHED_HIDDEN : COLOR_EMPTY;
}

// ============== Debug State ==============

/**
 * Debug toggle state - controls which visualizations are shown.
 */
export interface VoxelDebugState {
  showChunkBounds: boolean;
  showChunkCoords: boolean;
  showWireframe: boolean;
}

/**
 * Default debug state - all disabled
 */
export const DEFAULT_DEBUG_STATE: VoxelDebugState = {
  showChunkBounds: false,
  showChunkCoords: false,
  showWireframe: false,
};

// ============== Helper Creation Functions ==============

/**
 * Create a wireframe box for chunk boundaries.
 * @param chunk The chunk to create bounds for
 * @param state What this chunk costs versus what it contributes — see ChunkBoundsState
 * @returns LineSegments object representing chunk bounds
 */
export function createChunkBoundsHelper(chunk: Chunk, state: ChunkBoundsState): THREE.LineSegments {
  // A coarse chunk spans 2^lod base chunks per axis — the box must match ITS world extent
  // (getWorldPosition already scales the origin the same way).
  const size = CHUNK_WORLD_SIZE * (1 << chunk.lod);
  const geometry = new THREE.BoxGeometry(size, size, size);
  const edges = new THREE.EdgesGeometry(geometry);

  const color = chunkBoundsColor(state);
  const material = new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.7 });

  const wireframe = new THREE.LineSegments(edges, material);

  // Position at chunk center
  const worldPos = chunk.getWorldPosition();
  wireframe.position.set(
    worldPos.x + size / 2,
    worldPos.y + size / 2,
    worldPos.z + size / 2
  );
  
  // Store chunk info
  wireframe.userData.chunkKey = chunk.key;
  wireframe.userData.debugType = 'chunkBounds';
  
  return wireframe;
}

/**
 * Label text for a chunk: `x,y,z,lod`. Always four fields — the LOD is the reason to read the
 * label at all when the far rings are up, so it is not elided at lod 0.
 */
export function formatChunkLabel(chunk: Chunk): string {
  return `${chunk.cx},${chunk.cy},${chunk.cz},${chunk.lod}`;
}

/**
 * Create a text sprite showing chunk coordinates.
 * @param chunk The chunk to label
 * @returns Sprite with coordinate text, or null if DOM not available (Node.js)
 */
export function createChunkLabel(chunk: Chunk): THREE.Sprite | null {
  // Check if document exists (not available in Node.js)
  if (typeof document === 'undefined') {
    return null;
  }
  
  // Create canvas for text
  const canvas = document.createElement('canvas');
  const size = 256;
  canvas.width = size;
  canvas.height = size;
  
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
  ctx.fillRect(0, 0, size, size);

  // Tinted by the same palette the F7 LOD view uses, so the two views agree on sight.
  ctx.fillStyle = lodDebugCss(chunk.lod);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Fit to the canvas: four signed fields overflow 256px at the old fixed 48px.
  const text = formatChunkLabel(chunk);
  let fontPx = 48;
  do {
    ctx.font = `bold ${fontPx}px monospace`;
    if (ctx.measureText(text).width <= size * 0.92) break;
    fontPx -= 2;
  } while (fontPx > 16);
  ctx.fillText(text, size / 2, size / 2);
  
  // Create texture from canvas
  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  
  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
  });
  
  const sprite = new THREE.Sprite(material);

  // Position at chunk center (lod-scaled extent), label sized with the chunk so it stays
  // readable from the distances coarse chunks are viewed at.
  const chunkSize = CHUNK_WORLD_SIZE * (1 << chunk.lod);
  const worldPos = chunk.getWorldPosition();
  sprite.position.set(
    worldPos.x + chunkSize / 2,
    worldPos.y + chunkSize / 2,
    worldPos.z + chunkSize / 2
  );

  // Scale to reasonable size
  const labelScale = 2 * (1 << chunk.lod);
  sprite.scale.set(labelScale, labelScale, 1);
  
  // Store chunk info
  sprite.userData.chunkKey = chunk.key;
  sprite.userData.debugType = 'chunkLabel';
  
  return sprite;
}

// ============== VoxelDebugManager Class ==============

/**
 * Manages debug visualization for the voxel world.
 * Creates and removes debug objects based on toggle state.
 */
export class VoxelDebugManager {
  /** Reference to the scene */
  readonly scene: THREE.Scene;
  
  /** Current debug state */
  private state: VoxelDebugState = { ...DEFAULT_DEBUG_STATE };
  
  /** All debug objects, keyed by type and chunk key */
  private debugObjects: Map<string, THREE.Object3D> = new Map();
  
  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }
  
  /**
   * Update debug state and refresh visualizations.
   * @param newState Partial state update
   */
  setState(newState: Partial<VoxelDebugState>): void {
    const prevState = { ...this.state };
    this.state = { ...this.state, ...newState };
    
    // Handle toggling off - remove those visuals
    if (prevState.showChunkBounds && !this.state.showChunkBounds) {
      this.removeByType('chunkBounds');
    }
    if (prevState.showChunkCoords && !this.state.showChunkCoords) {
      this.removeByType('chunkLabel');
    }
  }
  
  /**
   * Get current debug state.
   */
  getState(): VoxelDebugState {
    return { ...this.state };
  }
  
  /**
   * Update debug visuals based on current chunks and meshes.
   * Call this after chunks load/unload.
   * @param chunks All loaded chunks
   * @param geometries All chunk geometries
   * @param isSelected The LOD walk's verdict for a chunk. It FILTERS the coord labels — without it
   *   every resident chunk is labelled, so an lod0 label and the lod1 label covering the same space
   *   stack and the `,lod` field is unreadable — and it COLOURS the bounds, which are still drawn for
   *   every resident chunk (see ChunkBoundsState: residency is what the boxes are for).
   */
  update(
    chunks: Map<string, Chunk>,
    geometries: Map<string, ChunkGeometry>,
    isSelected?: (chunkKey: string) => boolean,
  ): void {
    const anyFlagOn = this.state.showChunkBounds || this.state.showChunkCoords;

    // Fast path: nothing to create or clean up
    if (!anyFlagOn && this.debugObjects.size === 0) return;

    // Remove debug objects for unloaded chunks.
    if (this.debugObjects.size > 0) {
      const keysToRemove: string[] = [];
      for (const [objKey, obj] of this.debugObjects) {
        if (!chunks.has(obj.userData.chunkKey)) {
          keysToRemove.push(objKey);
        }
      }
      for (const key of keysToRemove) {
        const obj = this.debugObjects.get(key);
        if (obj) {
          this.disposeObject(obj);
          this.debugObjects.delete(key);
        }
      }
    }

    // Nothing to add if no debug flags are on
    if (!anyFlagOn) return;
    
    // Add/update debug objects for loaded chunks
    for (const [chunkKey, chunk] of chunks) {
      // Drawn is asked FIRST, not last: a chunk merged into a group can report hasGeometry() false
      // while it is plainly on screen, and calling that one 'unmeshed' would paint the working case
      // as the broken one.
      const state: ChunkBoundsState = (isSelected ? isSelected(chunkKey) : true) ? 'drawn'
        : (geometries.get(chunkKey)?.hasGeometry() ?? false) ? 'meshedHidden'
          : 'unmeshed';

      // Chunk bounds — every RESIDENT chunk, coloured by what it costs (see ChunkBoundsState).
      if (this.state.showChunkBounds) {
        this.ensureChunkBounds(chunk, state);
      }

      // Chunk labels — only for the LOD the walk actually selected here.
      if (this.state.showChunkCoords && (isSelected ? isSelected(chunkKey) : true)) {
        this.ensureChunkLabel(chunk);
      } else {
        this.removeDebugObject('chunkLabel', chunkKey);
      }
    }
  }
  
  /**
   * Ensure chunk bounds wireframe exists for a chunk.
   */
  private ensureChunkBounds(chunk: Chunk, state: ChunkBoundsState): void {
    const objKey = `chunkBounds:${chunk.key}`;
    const existing = this.debugObjects.get(objKey);

    if (existing) {
      // Update color if the chunk changed state (meshed, shown, or suppressed again)
      const mat = (existing as THREE.LineSegments).material as THREE.LineBasicMaterial;
      const targetColor = chunkBoundsColor(state);
      if (mat.color.getHex() !== targetColor) {
        mat.color.setHex(targetColor);
      }
      return;
    }

    const wireframe = createChunkBoundsHelper(chunk, state);
    this.debugObjects.set(objKey, wireframe);
    this.scene.add(wireframe);
  }
  
  /**
   * Ensure chunk label exists.
   */
  private ensureChunkLabel(chunk: Chunk): void {
    const objKey = `chunkLabel:${chunk.key}`;
    if (this.debugObjects.has(objKey)) return;
    
    const label = createChunkLabel(chunk);
    if (label) {
      this.debugObjects.set(objKey, label);
      this.scene.add(label);
    }
  }
  
  /**
   * Remove a specific debug object.
   */
  private removeDebugObject(type: string, chunkKey: string): void {
    const objKey = `${type}:${chunkKey}`;
    const obj = this.debugObjects.get(objKey);
    if (obj) {
      this.disposeObject(obj);
      this.debugObjects.delete(objKey);
    }
  }
  
  /**
   * Remove all debug objects of a specific type.
   */
  private removeByType(type: string): void {
    const keysToRemove: string[] = [];
    
    for (const [objKey, obj] of this.debugObjects) {
      if (obj.userData.debugType === type) {
        keysToRemove.push(objKey);
      }
    }
    
    for (const key of keysToRemove) {
      const obj = this.debugObjects.get(key);
      if (obj) {
        this.disposeObject(obj);
        this.debugObjects.delete(key);
      }
    }
  }
  
  /**
   * THE SEAM OVERLAY (F6): the ring/rim vertices and edges the stitcher actually considered.
   *
   * GREEN — reached a curve the zip works from. YELLOW — seeded, then rejected by a filter we can
   * name (`onDrawnBorder` found no one-sided edge for it, or its chain was a sub-3-vertex fragment).
   * A vertex bordering a hole with NO mark was never seeded at all, which is a different bug in a
   * different place: that is the distinction the two colours exist to draw.
   *
   * Depth testing OFF and no size attenuation, so the marks stay visible through terrain and stay
   * readable at any zoom — the whole point is to compare them against a hole you can see.
   *
   * Kept OUT of `debugObjects`, which is keyed per chunk and pruned when a chunk unloads. This is one
   * world-space overlay with no owning chunk.
   */
  private seamPoints: THREE.Points | null = null;
  private seamRejected: THREE.Points | null = null;
  private seamLines: THREE.LineSegments | null = null;
  private seamOpen: THREE.LineSegments | null = null;
  private seamCoarse: THREE.LineSegments | null = null;
  private seamMasked: THREE.LineSegments | null = null;
  private seamRimLow: THREE.Points | null = null;
  private seamVersion = -1;

  updateSeamDebug(
    on: boolean,
    buf: {
      pts: number[]; rejected: number[]; segs: number[]; open: number[]; coarse: number[];
      masked: number[]; rimLow: number[]; version: number;
    },
  ): void {
    if (!on) {
      for (const o of [this.seamPoints, this.seamRejected, this.seamLines, this.seamOpen,
        this.seamCoarse, this.seamMasked, this.seamRimLow]) {
        if (o) this.disposeObject(o);
      }
      this.seamPoints = null;
      this.seamRejected = null;
      this.seamLines = null;
      this.seamOpen = null;
      this.seamCoarse = null;
      this.seamMasked = null;
      this.seamRimLow = null;
      this.seamVersion = -1;
      return;
    }
    if (buf.version === this.seamVersion) return;
    this.seamVersion = buf.version;
    // WHITE POINTS = the seam's INPUT (fine rim vertices). Distinct from the green LINES, which are
    // rejections — they were both green, so a screenshot could not tell input from failure.
    this.seamPoints = this.rebuildPoints(this.seamPoints, buf.pts, 0xffffff, 3);
    this.seamRejected = this.rebuildPoints(this.seamRejected, buf.rejected, 0xffff00, 3);
    this.seamLines = this.rebuildLines(this.seamLines, buf.segs, 0x00ff00);
    // The outline of a hole INSIDE the band. Magenta: it has to read against green considered-edges,
    // yellow rejects and the red strip all at once.
    this.seamOpen = this.rebuildLines(this.seamOpen, buf.open, 0xff00ff);
    // THE SAME GAP FROM THE OTHER SIDE. Green and magenta both run along the FINE rim, so a gap whose
    // cause is on the coarse side had nothing marking it. Cyan is the coarse rim edge a failed link
    // would have bridged; where the coarse rim only has ONE of the two, a yellow point marks it, and
    // where there is neither cyan nor yellow the coarse rim is simply absent there.
    this.seamCoarse = this.rebuildLines(this.seamCoarse, buf.coarse, 0x00ffff);
    // GREY: a triangle the mask deleted. A gap sitting on grey was removed and not replaced.
    this.seamMasked = this.rebuildLines(this.seamMasked, buf.masked, 0x909090);
    // BLUE POINTS: the same input on a - face. White vs blue says which sign a gap is on.
    this.seamRimLow = this.rebuildPoints(this.seamRimLow, buf.rimLow, 0x2060ff, 3);
  }

  private rebuildPoints(
    prev: THREE.Points | null, data: readonly number[], color: number, size: number,
  ): THREE.Points | null {
    if (prev) this.disposeObject(prev);
    if (data.length === 0) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(Float32Array.from(data), 3));
    const m = new THREE.PointsMaterial({
      color, size, sizeAttenuation: false, depthTest: false, depthWrite: false, transparent: true,
    });
    const p = new THREE.Points(g, m);
    p.frustumCulled = false;
    p.renderOrder = 999;
    this.scene.add(p);
    return p;
  }

  private rebuildLines(
    prev: THREE.LineSegments | null, data: readonly number[], color: number,
  ): THREE.LineSegments | null {
    if (prev) this.disposeObject(prev);
    if (data.length === 0) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(Float32Array.from(data), 3));
    const m = new THREE.LineBasicMaterial({
      color, depthTest: false, depthWrite: false, transparent: true,
    });
    const l = new THREE.LineSegments(g, m);
    l.frustumCulled = false;
    l.renderOrder = 999;
    this.scene.add(l);
    return l;
  }

  /**
   * Dispose of a debug object and remove from scene.
   */
  private disposeObject(obj: THREE.Object3D): void {
    this.scene.remove(obj);
    
    // Dispose geometry and material
    if (obj instanceof THREE.LineSegments) {
      obj.geometry.dispose();
      (obj.material as THREE.Material).dispose();
    } else if (obj instanceof THREE.Mesh) {
      obj.geometry.dispose();
      (obj.material as THREE.Material).dispose();
    } else if (obj instanceof THREE.Sprite) {
      (obj.material as THREE.SpriteMaterial).map?.dispose();
      obj.material.dispose();
    }
  }
  
  /**
   * Get count of debug objects.
   */
  getDebugObjectCount(): number {
    return this.debugObjects.size;
  }
  
  /**
   * Get stats about debug visualizations.
   */
  getStats(): { totalObjects: number; chunkBounds: number; chunkLabels: number } {
    let chunkBounds = 0;
    let chunkLabels = 0;

    for (const obj of this.debugObjects.values()) {
      switch (obj.userData.debugType) {
        case 'chunkBounds': chunkBounds++; break;
        case 'chunkLabel': chunkLabels++; break;
      }
    }

    return { totalObjects: this.debugObjects.size, chunkBounds, chunkLabels };
  }
  
  /**
   * Dispose of all debug objects.
   */
  dispose(): void {
    for (const obj of this.debugObjects.values()) {
      this.disposeObject(obj);
    }
    this.debugObjects.clear();
  }
}
